// Module teasers: the sellable Lumi modules a workspace does NOT have.
//
// The app launcher shows them as non-navigable upsell tiles beside the modules the
// user may actually open. This is deliberately a SEPARATE concept from a
// ModuleSwitchTarget (moduleSwitcher.ts): a teaser carries no `href` and is not
// assignable to a switch target, so no refactor can quietly turn "you could buy
// this" into a link the user is not entitled to follow.
//
// Everything here is fail-closed in the same shape as the switcher: when we cannot
// confirm what the workspace owns we show NOTHING. The worst outcome of this
// feature is an unconfirmed access payload rendering as a wall of upsells for every
// module — so `null` access must never be read as "owns nothing".
import type { LumiModuleKey, ModuleSwitchTarget, ModuleTeaser } from '@lumi/ui';
import { hasTenantAdminAccess, type TenantModuleAccess } from './moduleSwitcher';
import { moduleDescription, moduleName } from './moduleNames';

// The teaser SHAPE is owned by @lumi/ui's ModuleBadge, which renders it — exactly
// like ModuleSwitchTarget above. Re-exported here so consumers can take the type
// and the builder from one import, but there is only ever ONE definition: a second
// declaration of the same concept is how the renderer and the builder drift.
export type { ModuleTeaser };

// Faithful port of `isModulePresent` in system-admin-api/src/plans/plans.service.ts,
// which is the source of truth: it is the predicate behind add-module's
// MODULE_ALREADY_ACTIVE 409 and behind `addableModules`. Ported rather than imported
// because ADR 0001 forbids shared runtime code across services — a drift here shows
// up as a teaser whose "add module" call the API then rejects, so the statuses must
// be changed in both places or in neither.
//
// `provisioning` and `failed` count as PRESENT because both are already paid for
// (add-module provisions before it charges, and ADR 0016's reconciler re-drives a
// failed row). Teasing either would invite a second purchase of an owned module.
// Only `inactive` and an absent row are (re-)addable, hence teasable.
const PRESENT_MODULE_STATUSES = ['active', 'trial', 'provisioning', 'failed'];

function isModulePresent(status: string | null | undefined): boolean {
  return !!status && PRESENT_MODULE_STATUSES.includes(status);
}

// EVERY sellable module, in the order teasers are shown — the UI-side mirror of
// system-admin-api's MODULE_KEYS (plan-limits.ts), each mapped from the user-api
// module namespace to the switcher's LumiModuleKey.
//
// A module that is not sold is simply absent from this list. That absence is
// structural on purpose: there is no filter to forget, and the only way to tease
// a module is to add it here. The core modules every workspace has (`people`,
// `payroll`) are never sold, so they can never be teased.
/**
 * The user-api key of every module Lumi sells. A UNION rather than `string`, so a
 * surface that must render one per key — the workspace home's teaser card has an
 * icon, a hue and a description per module — fails to compile when this list grows
 * instead of silently falling back to a generic tile.
 */
export type SellableModuleKey = 'ats' | 'time_tracking' | 'apprenticeship';

// Every entry is also somewhere you can go (`key`), so the launcher and the
// workspace home tease the same list in two namespaces. A sellable module that is
// NOT a place would need `key` to become nullable again and the launcher to filter
// it out — deliberately not pre-built while there is none.
const SELLABLE_MODULES: ReadonlyArray<{ apiKey: SellableModuleKey; key: LumiModuleKey }> = [
  { apiKey: 'ats', key: 'ats' },
  { apiKey: 'time_tracking', key: 'time' },
  { apiKey: 'apprenticeship', key: 'apprentice' },
];

/**
 * The api-namespace module keys the workspace already has, or null when there is
 * nothing to decide from. THE fail-closed core of this file: `null` means UNKNOWN
 * and every caller must then tease nothing.
 *
 * THE TRAP: this derives ONLY from `access.modules`, the workspace's entitlement
 * rows. It must never reuse `accessibleModuleKeys()` / `isModuleAccessible()`, which
 * additionally apply this USER's assignments and the module's assignment policy. A
 * workspace that owns Zeit but has not assigned this user to it would otherwise be
 * offered Zeit for sale — an upsell for something it already pays for, shown to
 * someone whose colleague uses it daily.
 *
 * An empty `modules` array is NOT unknown: it is a real workspace with no module
 * rows, so what is sellable is teasable — with one exception, below.
 */
function presentModuleKeys(access: TenantModuleAccess | null | undefined): Set<string> | null {
  if (!access) return null;
  const rows = access.modules || [];
  const present = new Set(rows.filter((m) => isModulePresent(m.status)).map((m) => m.moduleKey));

  // The legacy bootstrap quirk, which must be honoured HERE rather than papered
  // over downstream: the switcher's `isModuleActive` treats a workspace with no
  // module rows at all as having `ats`. Subtracting the offered switch targets in
  // buildModuleTeasers is not enough to keep the two views agreeing, because that
  // list is a USER-level verdict — a per-user `deny` on ats empties it, and the
  // teaser would then be shown to a member of a workspace whose colleagues use
  // Recruiting every day. This is the workspace-level verdict, so it belongs to
  // the workspace-level question. Being narrower than the server's addable set
  // (which has no such quirk) is safe: we never offer what the POST refuses.
  if (rows.length === 0) present.add('ats');

  // The showroom (#409 M3): a module the demo has opened without a booking is
  // PRESENT for the length of the demo — it is on the launcher as a place to go,
  // and teasing it beside itself would offer for sale what is visibly open. It is
  // still not owned: the exit gate, not the launcher, is where the upgrade is
  // offered, and it reads the server's own `addableModules`.
  for (const key of access.showroomModules || []) present.add(key);

  return present;
}

/**
 * Every sellable module this WORKSPACE does not own, in the user-api namespace.
 * The workspace home's teaser set (issue 261 M2).
 *
 * Null on unknown access, like every other reader here. Callers must render
 * nothing for null, never an empty-set-shaped "owns nothing".
 */
export function teasableSellableModuleKeys(
  access: TenantModuleAccess | null | undefined,
): SellableModuleKey[] | null {
  const present = presentModuleKeys(access);
  if (!present) return null;
  return SELLABLE_MODULES.filter((m) => !present.has(m.apiKey)).map((m) => m.apiKey);
}

/**
 * The same set in the switcher's namespace, for the LAUNCHER: sellable modules the
 * workspace does not own, as places a user could be sent. Same fail-closed null
 * contract.
 */
export function teasableModuleKeys(access: TenantModuleAccess | null | undefined): LumiModuleKey[] | null {
  const present = presentModuleKeys(access);
  if (!present) return null;
  return SELLABLE_MODULES.filter((m) => !present.has(m.apiKey)).map((m) => m.key);
}

/**
 * Build the launcher's teaser list.
 *
 * Fail-closed and symmetrical with `buildModuleSwitchTargets`: a null/undefined
 * candidate set (unknown access) yields NO teasers.
 *
 * `offered` is the switch-target list the same launcher renders. Subtracting it is
 * belt and braces — the two lists are derived from different predicates, so this is
 * the one place that can guarantee a module is never shown as an owned tile and an
 * upsell at the same time, whatever upstream does.
 *
 * `billingHref` is attached to every teaser only when the caller supplies a non-empty
 * one. The CALLER decides that by capability (see canManageBilling); this function
 * deliberately does not re-derive a role test, so there is one predicate, not two.
 */
export function buildModuleTeasers(
  teasable: LumiModuleKey[] | null | undefined,
  offered: ModuleSwitchTarget[],
  opts: { billingHref?: string | null } = {},
): ModuleTeaser[] {
  if (!teasable) return [];
  const wanted = new Set(teasable);
  const offeredKeys = new Set((offered || []).map((t) => t.key));
  const billingHref = typeof opts.billingHref === 'string' && opts.billingHref ? opts.billingHref : undefined;

  // Iterating the registry (not the caller's array) keeps the order stable and
  // deduplicated regardless of how the candidate set was assembled, and makes a key
  // that is not sellable — the core modules — unrepresentable here.
  const out: ModuleTeaser[] = [];
  for (const { key } of SELLABLE_MODULES) {
    if (!wanted.has(key) || offeredKeys.has(key)) continue;
    const words = { key, label: moduleName(key), description: moduleDescription(key) };
    out.push(billingHref ? { ...words, billingHref } : words);
  }
  return out;
}

/**
 * May the user change this workspace's subscription? THE single predicate for that
 * question suite-wide (workspace-ui's tenants store delegates to it rather than
 * keeping its own copy), so the teaser's control, the Billing nav item and
 * payment-api's own gate cannot drift apart.
 *
 * The billing-manager set is ONE set (ADR 0033): the tenant OWNER, a
 * `billing_manager` holder, or a `suite_admin` — exactly what payment-api's
 * hasUserTenantAdminMembership accepts, so the control never leads to a 403. The
 * `isOwner` fallback exists only for payloads that predate the field. Fail-closed:
 * unknown access carries no billing control.
 */
export function canManageBilling(access: TenantModuleAccess | null | undefined): boolean {
  if (!access) return false;
  const isOwner = typeof access.isOwner === 'boolean' ? access.isOwner : hasTenantAdminAccess(access);
  return isOwner || access.isBillingManager === true || access.isSuiteAdmin === true;
}
